import { ReactNode, useEffect, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import {
  faIdBadge,
  faDiagramProject,
  faClockRotateLeft,
  faQuestionCircle,
  faCircleInfo,
  faCircleCheck,
  faChevronUp,
  faChevronDown,
} from '@fortawesome/pro-regular-svg-icons'
import Typography from '@mui/material/Typography'
import Switch from '@mui/material/Switch'
import Tooltip from '@mui/material/Tooltip'
import { useDispatch } from 'react-redux'
import { updateAppAlert } from '@labrav/common'
import {
  FundingSettingsAuditEntry,
  FundingSettings as FundingSettingsData,
  FundingSettingsAutomation,
  getFundingSettings,
  patchFundingSettings,
} from '../../api/fundingApi'
import { formatActivityTime } from '../../utils/format'
import { ScrollHint } from '../ScrollHint/ScrollHint'
import { AutoSubmitConfirmDialog } from './AutoSubmitConfirmDialog'
import { AutoSubmitLimitNote } from './AutoSubmitLimitNote'
import { AUDIT_WATERMARK_SVG } from './auditWatermark'
import { useStyles } from './FundingSettings.styles'

// Wrap an info icon in a tooltip only when the server provided copy for it.
const InfoIcon = ({
  className,
  tooltip,
}: {
  className: string
  tooltip?: ReactNode
}) => {
  const icon = <FontAwesomeIcon icon={faCircleInfo} className={className} />
  return tooltip ? (
    <Tooltip title={tooltip} arrow placement="top">
      <span>{icon}</span>
    </Tooltip>
  ) : (
    icon
  )
}

interface FundingSettingsProps {
  serviceUrl?: string
  partnerId?: string
  /**
   * Identity of the person making changes, threaded from the host so the
   * backend can attribute settings-audit entries. Omitted in contexts with no
   * signed-in user.
   */
  updaterName?: string
  updaterLabraUserId?: string
  /** Fires when the "Configure notifications" button is pressed. */
  onConfigureNotifications?: () => void
}

// The server may append its own limit aside; only its first line, the
// condition, is shown, and AutoSubmitLimitNote states the limit.
const autoSubmitCondition = (criteria?: string | null) =>
  criteria?.split('\n')[0].trim() ?? ''

const automationKey = (a: FundingSettingsAutomation) =>
  `${a.program}:${a.program_label}`

const programNoun = (programLabel: string) =>
  programLabel.replace(/\s+funding program$/i, '')

const confirmCopy = (programLabel: string, enabling: boolean) =>
  enabling
    ? {
        title: `Enable auto-submit for ${programLabel}?`,
        bodyLines: [
          `Are you sure you want to enable auto-submit for ${programNoun(programLabel)} funding program? Your eligible funding requests will be submitted automatically.`,
        ],
      }
    : {
        title: `Disable auto-submit for ${programLabel}?`,
        bodyLines: [
          `Are you sure you want to disable auto-submit for ${programNoun(programLabel)} funding program? You will have to manually submit your funding requests for this funding program.`,
        ],
      }

const PAYEE_ID_HELPER = 'E.g. 3DFKGLRDZSD32'
// An AWS payee id is exactly this long, so the input caps typing there and a
// shorter value is reported rather than saved.
const PAYEE_ID_LENGTH = 13
const PAYEE_ID_ERROR = `Payee ID must be ${PAYEE_ID_LENGTH} characters`
// Long enough that a PATCH (and its audit-log entry) is not written on every
// pause while typing the id out.
export const PAYEE_AUTOSAVE_DELAY_MS = 1000
const PAYEE_HELPER_ID = 'payee-id-helper'

interface CollapsibleCardProps {
  icon: IconDefinition
  title: string
  help?: boolean
  /** Server-provided copy for the header help icon; no tooltip when absent. */
  helpTooltip?: string | null
  children: ReactNode
}

const CollapsibleCard = ({
  icon,
  title,
  help,
  helpTooltip,
  children,
}: CollapsibleCardProps) => {
  const { classes } = useStyles()
  const [open, setOpen] = useState(true)
  const helpIcon = (
    <FontAwesomeIcon icon={faQuestionCircle} className={classes.helpIcon} />
  )
  return (
    <section className={classes.card}>
      <div className={classes.cardHeader}>
        <div className={classes.headerLeft}>
          <div className={classes.headerTitleGroup}>
            <span className={classes.iconBox}>
              <FontAwesomeIcon icon={icon} />
            </span>
            <Typography component="h2" className={classes.cardTitle}>
              {title}
            </Typography>
          </div>
          {help &&
            (helpTooltip ? (
              <Tooltip title={helpTooltip} arrow placement="top">
                <span>{helpIcon}</span>
              </Tooltip>
            ) : (
              helpIcon
            ))}
        </div>
        <button
          type="button"
          className={classes.collapseButton}
          aria-label={open ? 'Collapse' : 'Expand'}
          aria-expanded={open}
          onClick={() => setOpen(prev => !prev)}
        >
          <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} />
        </button>
      </div>
      {open && children}
    </section>
  )
}

export const FundingSettings = ({
  serviceUrl,
  partnerId,
  updaterName,
  updaterLabraUserId,
  onConfigureNotifications,
}: FundingSettingsProps) => {
  const { classes, cx } = useStyles()
  const dispatch = useDispatch()
  const [payeeId, setPayeeId] = useState('')
  const [savedPayeeId, setSavedPayeeId] = useState('')
  const [payeeStatus, setPayeeStatus] = useState<'idle' | 'saved'>('idle')
  // The error only appears once they leave the field, so typing towards the
  // full length is not flagged on every keystroke.
  const [payeeError, setPayeeError] = useState(false)
  const payeeSaveTimer = useRef<ReturnType<typeof setTimeout>>()
  const [automation, setAutomation] = useState<FundingSettingsAutomation[]>([])
  const [auditLog, setAuditLog] = useState<FundingSettingsAuditEntry[]>([])
  const auditRef = useRef<HTMLDivElement>(null)
  const [pending, setPending] = useState<{ key: string; next: boolean } | null>(
    null
  )

  useEffect(() => {
    if (!serviceUrl || !partnerId) return
    let ignore = false
    ;(async () => {
      try {
        const { data } = await getFundingSettings(serviceUrl, partnerId)
        if (ignore) return
        setPayeeId(data.payee_id ?? '')
        setSavedPayeeId(data.payee_id ?? '')
        setAutomation(data.automation ?? [])
        setAuditLog(data.audit_log ?? [])
      } catch {
        // leave fields empty; the page still renders
      }
    })()
    return () => {
      ignore = true
    }
  }, [serviceUrl, partnerId])

  const save = (
    body: Partial<FundingSettingsData>,
    { rollback, onSaved }: { rollback?: () => void; onSaved?: () => void } = {}
  ) => {
    if (!serviceUrl || !partnerId) return
    // Attribute the change so the backend records who updated the settings: the
    // display name (falls back to the Labra user id) plus the raw Labra user id.
    const actor = updaterName || updaterLabraUserId
    const patch = {
      ...body,
      ...(actor ? { updated_by: actor } : {}),
      ...(updaterLabraUserId
        ? { updated_by_labra_user_id: updaterLabraUserId }
        : {}),
    }
    patchFundingSettings(serviceUrl, partnerId, patch)
      .then(({ data }) => {
        // The PATCH returns the fresh settings (incl. the new audit entry).
        if (data?.audit_log) setAuditLog(data.audit_log)
        // An auto-saved field reports inline instead, so it opts out of the
        // toast rather than popping one every time typing settles.
        if (onSaved) {
          onSaved()
          return
        }
        dispatch(
          updateAppAlert({
            message: 'Funding settings updated successfully.',
            messageType: 'SUCCESS',
            autoClose: true,
          })
        )
      })
      .catch(error => {
        // Roll the optimistic change back so the UI reflects the persisted
        // (unchanged) server state instead of a value that never saved.
        rollback?.()
        const message =
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.message ||
          'Failed to update funding settings.'
        dispatch(
          updateAppAlert({ message, messageType: 'ERROR', autoClose: true })
        )
      })
  }

  const isCompletePayee = (value: string) =>
    value.trim().length === PAYEE_ID_LENGTH

  const savePayee = (value: string) => {
    // Empty clears nothing: leaving the field blank is treated as "not filled
    // in yet", never as a request to wipe the stored id.
    if (!isCompletePayee(value) || value === savedPayeeId) return
    const previous = savedPayeeId
    setSavedPayeeId(value)
    save(
      { payee_id: value },
      {
        onSaved: () => setPayeeStatus('saved'),
        rollback: () => {
          setSavedPayeeId(previous)
          setPayeeId(previous)
          setPayeeStatus('idle')
        },
      }
    )
  }

  const cancelPayeeSave = () => clearTimeout(payeeSaveTimer.current)

  const onPayeeChange = (value: string) => {
    setPayeeId(value)
    setPayeeStatus('idle')
    if (isCompletePayee(value) || !value.trim()) setPayeeError(false)
    cancelPayeeSave()
    payeeSaveTimer.current = setTimeout(
      () => savePayee(value),
      PAYEE_AUTOSAVE_DELAY_MS
    )
  }

  // Leaving the field saves right away rather than waiting out the delay, and
  // is where an incomplete id is called out.
  const onPayeeBlur = () => {
    cancelPayeeSave()
    const value = payeeId.trim()
    setPayeeError(value.length > 0 && value.length !== PAYEE_ID_LENGTH)
    savePayee(payeeId)
  }

  useEffect(() => cancelPayeeSave, [])

  const setAutoSubmit = (key: string, value: boolean) => {
    const previous = automation
    const next = automation.map(a =>
      automationKey(a) === key ? { ...a, auto_submit: value } : a
    )
    setAutomation(next)
    save({ automation: next }, { rollback: () => setAutomation(previous) })
  }

  // Both directions confirm via the shared dialog.
  const onToggleAutoSubmit = (item: FundingSettingsAutomation) =>
    setPending({ key: automationKey(item), next: !item.auto_submit })

  const pendingProgram = automation.find(a => automationKey(a) === pending?.key)

  const confirmPending = () => {
    if (pending) setAutoSubmit(pending.key, pending.next)
    setPending(null)
  }

  const copy =
    pendingProgram && pending
      ? confirmCopy(pendingProgram.program_label, pending.next)
      : { title: '', bodyLines: [] }

  return (
    <div className={classes.root} data-testid="funding-settings">
      <div className={classes.leftColumn}>
        <CollapsibleCard icon={faIdBadge} title="Payee ID">
          <div className={classes.inputWrap}>
            <div className={classes.fieldRow}>
              <div
                className={cx(
                  classes.textField,
                  payeeError && classes.textFieldError
                )}
              >
                <input
                  className={classes.input}
                  placeholder="Payee ID"
                  aria-label="Payee ID"
                  maxLength={PAYEE_ID_LENGTH}
                  value={payeeId}
                  aria-invalid={payeeError}
                  aria-describedby={PAYEE_HELPER_ID}
                  onChange={event => onPayeeChange(event.target.value)}
                  onBlur={onPayeeBlur}
                />
                <InfoIcon
                  className={classes.inputInfoIcon}
                  tooltip={
                    <>
                      AWS Payee ID is your company’s identifier in Amazon Payee
                      Central, used for cash payouts/invoices from AWS/Amazon
                      funding programs.{' '}
                      <a
                        href="https://helpcenter.labra.io/hc/en-us/articles/54258496339475-How-to-find-your-AWS-Payee-ID"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.tooltipLink}
                      >
                        How to find your AWS Payee ID
                      </a>
                    </>
                  }
                />
              </div>
              {payeeStatus === 'saved' && (
                <span
                  className={classes.savedBadge}
                  data-testid="payee-id-saved"
                >
                  <FontAwesomeIcon icon={faCircleCheck} />
                  <Typography component="span">Saved</Typography>
                </span>
              )}
            </div>
            <Typography
              component="span"
              id={PAYEE_HELPER_ID}
              className={cx(
                classes.helperText,
                payeeError && classes.helperTextError
              )}
            >
              {payeeError ? PAYEE_ID_ERROR : PAYEE_ID_HELPER}
            </Typography>
          </div>
        </CollapsibleCard>

        <CollapsibleCard
          icon={faDiagramProject}
          title="Auto-submit funding request with AI"
          help
          helpTooltip="As soon as conditions are met, Labra AI will submit the Funding Request for you automatically."
        >
          <div className={classes.automationBody}>
            {automation.map((program, index) => {
              const key = automationKey(program)
              return (
                <div key={key}>
                  {index > 0 && <span className={classes.rowDivider} />}
                  <div className={classes.automationRow}>
                    <div className={classes.rowMain}>
                      <span className={classes.rowLabelLine}>
                        <Typography
                          component="span"
                          className={classes.rowLabel}
                        >
                          {program.program_label}
                        </Typography>
                        <InfoIcon
                          className={classes.rowInfoIcon}
                          tooltip={program.description}
                        />
                      </span>
                      <Typography
                        component="span"
                        className={classes.rowDescription}
                      >
                        {autoSubmitCondition(program.auto_submit_criteria)}
                      </Typography>
                      <AutoSubmitLimitNote
                        limitUsd={program.auto_submission_limit_usd}
                        className={classes.rowCriteriaAside}
                      />
                    </div>
                    <Switch
                      checked={program.auto_submit}
                      onChange={() => onToggleAutoSubmit(program)}
                      inputProps={{ 'aria-label': program.program_label }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </CollapsibleCard>
      </div>

      <div className={classes.rightColumn}>
        <div className={classes.sideCard}>
          <div className={classes.sideCardHeader}>
            <span className={classes.iconBox}>
              <FontAwesomeIcon icon={faClockRotateLeft} />
            </span>
            <Typography component="h2" className={classes.sideCardTitle}>
              Audit logs
            </Typography>
          </div>
          {auditLog.length > 0 ? (
            <div className={classes.auditScrollWrap}>
              <div ref={auditRef} className={classes.auditList}>
                {[...auditLog].reverse().map((entry, index) => {
                  const time = formatActivityTime(entry.event_at)
                  const meta = [entry.actor, time].filter(Boolean).join(' · ')
                  return (
                    <div key={entry.id}>
                      {index > 0 && <span className={classes.auditDivider} />}
                      <div className={classes.auditItem}>
                        <Typography
                          component="span"
                          className={classes.auditItemText}
                        >
                          {entry.summary}
                        </Typography>
                        {meta && (
                          <Typography
                            component="span"
                            className={classes.auditItemTime}
                          >
                            {meta}
                          </Typography>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
              <ScrollHint scrollRef={auditRef} contentKey={auditLog.length} />
            </div>
          ) : (
            <div className={classes.auditEmpty}>
              <img
                src={AUDIT_WATERMARK_SVG}
                alt=""
                data-testid="audit-watermark"
                className={classes.auditWatermark}
              />
            </div>
          )}
        </div>

        <div className={classes.sideCard}>
          <div className={classes.sideCardHeader}>
            <span className={classes.iconBox}>
              <FontAwesomeIcon icon={faClockRotateLeft} />
            </span>
            <Typography component="h2" className={classes.sideCardTitle}>
              Associated notifications
            </Typography>
          </div>
          <div className={classes.notificationsBody}>
            <Typography component="span" className={classes.notificationsText}>
              Go to Notifications to set up notifications for your funding
              updates.
            </Typography>
            <button
              type="button"
              className={classes.notificationsButton}
              onClick={onConfigureNotifications}
            >
              Configure notifications
            </button>
          </div>
        </div>
      </div>

      <AutoSubmitConfirmDialog
        open={pendingProgram != null}
        title={copy.title}
        bodyLines={copy.bodyLines}
        limitUsd={pendingProgram?.auto_submission_limit_usd}
        onCancel={() => setPending(null)}
        onProceed={confirmPending}
      />
    </div>
  )
}

export default FundingSettings
